/**
 * AdminSideNav — permission-aware grouped navigation (STORY-008-006). Maps
 * ADMIN_NAV, filtering items by the caller's permissions (from /me) and hiding
 * empty groups. Ports the seller-applications pending badge. Server RBAC is the
 * real enforcement; hiding here is UX. min-h-44px tap targets; --g-* tokens.
 */
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { ADMIN_NAV } from "@/features/admin/navConfig";
import { useQueueCounts } from "@/features/admin/hooks/useSellerApplications";
import { useMe } from "@/features/settings/api";

/** Pending count = submitted + in_review (mirrors the queue badge). */
function usePendingCount(): number {
  const { data } = useQueueCounts();
  if (!data) return 0;
  return (data.submitted ?? 0) + (data.in_review ?? 0);
}

export function AdminSideNav({
  onNavigate,
}: {
  onNavigate?: () => void;
}): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data: profile } = useMe();
  const permissions: string[] = profile?.permissions ?? [];
  const pendingCount = usePendingCount();

  return (
    <nav
      data-testid="admin-side-nav"
      aria-label={t("nav.ariaLabel")}
      className="flex w-56 flex-col gap-4 overflow-y-auto border-r border-[var(--g-hairline)] bg-[var(--g-surface)] p-3"
    >
      {ADMIN_NAV.map((group) => {
        const items = group.items.filter((item) =>
          permissions.includes(item.requiredPermission),
        );
        if (items.length === 0) return null;
        return (
          <div key={group.id} data-testid={`admin-nav-group-${group.id}`}>
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--g-secondary)]">
              {t(group.labelKey)}
            </p>
            <ul className="flex flex-col gap-0.5" role="list">
              {items.map((item) => {
                const IconCmp = item.icon;
                return (
                  <li key={item.id}>
                    <NavLink
                      to={item.to}
                      data-testid={`admin-nav-${item.id}`}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        [
                          "flex min-h-[44px] items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
                          isActive
                            ? "bg-[var(--g-primary-muted,color-mix(in_srgb,var(--g-primary)_12%,transparent))] text-[var(--g-primary)]"
                            : "text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] hover:text-foreground",
                        ].join(" ")
                      }
                    >
                      <IconCmp size={18} strokeWidth={1.75} aria-hidden />
                      <span className="flex-1 truncate">{t(item.labelKey)}</span>
                      {item.id === "seller-applications" && pendingCount > 0 ? (
                        <span
                          data-testid="admin-nav-badge"
                          aria-label={t("nav.pendingCount", {
                            count: pendingCount,
                          })}
                          className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[var(--g-primary)] px-1.5 text-[11px] font-semibold text-[var(--g-on-primary)]"
                        >
                          {pendingCount > 99 ? "99+" : pendingCount}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

export default AdminSideNav;
